import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, MenuItem, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SubjectIcon from "@mui/icons-material/Subject";
import { COLLECTION_PAGE_SIZE, fetchCollectionHistory } from "./gestionCarteraApi";
import { formatDate } from "./gestionCarteraFormat";
import {
  attemptChannels,
  CHANNELS,
  formatTime,
  groupHistoryByDay,
  parseAdvisor,
  RESULTS,
} from "./gestionCobranzaCatalog";
import { COLORS, labelSx, PrimaryButton, StatusBadge } from "./gestionCarteraUi";
import TranscripcionModal from "./modals/TranscripcionModal";

const EMPTY_FILTERS = { creditUuid: "", result: "", channel: "", from: "", to: "" };
const CHANNEL_ICONS = {
  LLAMADA_IA: SmartToyOutlinedIcon,
  LLAMADA_ASESOR: PhoneInTalkOutlinedIcon,
  WHATSAPP: WhatsAppIcon,
  OTRO: ChatBubbleOutlineIcon,
};

function ChannelIcon({ channel }) {
  const Icon = CHANNEL_ICONS[channel] || CHANNEL_ICONS.OTRO;
  return (
    <Tooltip title={CHANNELS[channel]?.label || CHANNELS.OTRO.label}>
      <Box sx={{ width: 36, height: 36, flex: "none", borderRadius: "50%", backgroundColor: "#EEF1F6", color: COLORS.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon fontSize="small" aria-label={CHANNELS[channel]?.label} />
      </Box>
    </Tooltip>
  );
}

function ManagementNotes({ notes }) {
  const [expanded, setExpanded] = useState(false);
  if (!notes) return null;
  const long = notes.length > 160;
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 14,
          ...(long && !expanded && { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }),
        }}
      >
        {notes}
      </Typography>
      {long && (
        <Box component="button" type="button" onClick={() => setExpanded((current) => !current)} sx={{ border: 0, background: "none", p: 0, mt: 0.5, color: COLORS.primary, fontWeight: 500, cursor: "pointer", font: "inherit", fontSize: 14 }}>
          {expanded ? "Ver menos" : "Ver más"}
        </Box>
      )}
    </Box>
  );
}

function ManagementItem({ management, onTranscript }) {
  const result = RESULTS[management.result] || RESULTS.OTRO;
  const advisor = parseAdvisor(management.advisorName, management.channel);
  const canSeeTranscript = management.channel === "LLAMADA_IA" && management.externalCallId;

  return (
    <Box component="li" sx={{ display: "flex", gap: 1.5, py: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
      <ChannelIcon channel={management.channel} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{formatTime(management.managementTime) || "—"}</Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>{`· Crédito ${management.creditOldId} · ${CHANNELS[management.channel]?.label || CHANNELS.OTRO.label}`}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 3, rowGap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={labelSx}>Tipo de gestión</Typography>
            <StatusBadge tone={result.tone} label={result.label} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={labelSx}>Estado</Typography>
            <Typography sx={{ fontWeight: 500 }}>{management.statusRaw?.trim() || "—"}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontSize: 14, color: COLORS.muted }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>{advisor.name}</Typography>
          {advisor.isAi && <StatusBadge tone="purple" label="IA" />}
          {advisor.role && <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{advisor.role}</Typography>}
        </Box>
        <ManagementNotes notes={management.notes} />
        {(management.commitmentDate || canSeeTranscript) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            {management.commitmentDate && (
              <StatusBadge tone="blue" label={`Compromiso: ${formatDate(management.commitmentDate)} · Cuota ${management.commitmentQuotaNumber ?? "—"}`} />
            )}
            {canSeeTranscript && (
              <PrimaryButton startIcon={<SubjectIcon />} onClick={() => onTranscript(management)} sx={{ minHeight: 34, px: 1.5, fontSize: 14 }}>
                Ver transcripción
              </PrimaryButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function AttemptsBlock({ items, onTranscript }) {
  const [open, setOpen] = useState(false);
  const times = items.map((item) => formatTime(item.managementTime)).filter(Boolean).sort();
  const range = times.length ? `${times[0]} – ${times[times.length - 1]}` : "";
  return (
    <Box component="li" sx={{ borderTop: `1px solid ${COLORS.border}` }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 0, border: 0, background: "none", cursor: "pointer", font: "inherit", textAlign: "left", color: COLORS.text }}
      >
        <Box sx={{ width: 36, height: 36, flex: "none", borderRadius: "50%", backgroundColor: "#F2F4F7", color: COLORS.muted, display: "flex", alignItems: "center", justifyContent: "center", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
          <ExpandMoreIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontWeight: 500 }}>{`${items.length} intentos sin contacto`}</Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>{`· ${attemptChannels(items).join(", ")}${range ? ` · ${range}` : ""}`}</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, pl: { xs: 0, sm: 6 } }}>
          {items.map((item) => <ManagementItem key={item.uuid} management={item} onTranscript={onTranscript} />)}
        </Box>
      </Collapse>
    </Box>
  );
}

function HistoryFilters({ filters, credits, onChange, onClear }) {
  const set = (name) => (event) => onChange({ ...filters, [name]: event.target.value });
  const hasFilters = Object.values(filters).some(Boolean);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(5, 1fr) auto" }, gap: 1.5, alignItems: "center" }}>
      <TextField select size="small" label="Crédito" value={filters.creditUuid} onChange={set("creditUuid")}>
        <MenuItem value="">Todos</MenuItem>
        {credits.map((credit) => <MenuItem key={credit.uuid} value={credit.uuid}>{`Crédito ${credit.oldId}`}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Tipo de gestión" value={filters.result} onChange={set("result")}>
        <MenuItem value="">Todos</MenuItem>
        {["ACUERDO_PAGO", "EFECTIVO", "CONTACTO", "INTENTO"].map((code) => <MenuItem key={code} value={code}>{RESULTS[code].label}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Canal" value={filters.channel} onChange={set("channel")}>
        <MenuItem value="">Todos</MenuItem>
        {["LLAMADA_IA", "LLAMADA_ASESOR", "WHATSAPP"].map((code) => <MenuItem key={code} value={code}>{CHANNELS[code].label}</MenuItem>)}
      </TextField>
      <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={filters.from} onChange={set("from")} />
      <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={filters.to} onChange={set("to")} />
      <PrimaryButton onClick={onClear} disabled={!hasFilters} sx={{ minHeight: 40 }}>Limpiar filtros</PrimaryButton>
    </Box>
  );
}

/** Historial paginado: "Cargar más" agrega la siguiente página; cambiar filtros reinicia. */
const useCollectionHistory = (clientUuid, filters) => {
  const [state, setState] = useState({ status: "loading", items: [], page: 0, last: true, totalElements: 0 });
  const latestRequest = useRef(0);

  const load = useCallback(
    async (page) => {
      const requestId = ++latestRequest.current;
      setState((current) => ({ ...current, status: page === 0 ? "loading" : "loadingMore" }));
      try {
        const data = await fetchCollectionHistory({ clientUuid, filters, page });
        if (requestId !== latestRequest.current) return;
        setState((current) => ({
          status: "ready",
          items: page === 0 ? data.content : [...current.items, ...data.content],
          page,
          last: data.last,
          totalElements: data.totalElements,
        }));
      } catch (error) {
        if (requestId === latestRequest.current) setState((current) => ({ ...current, status: "error" }));
      }
    },
    [clientUuid, filters]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return [state, load];
};

/**
 * Gestión de cobranza del cliente: un solo historial de gestiones agrupado por día, con su tipo de
 * gestión. `credits`: todos los créditos del cliente (para el filtro por crédito).
 */
export default function GestionCobranzaSection({ clientUuid, credits }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openModal, setOpenModal] = useState({ name: null, management: null });
  const [history, loadHistory] = useCollectionHistory(clientUuid, filters);

  const groups = useMemo(() => groupHistoryByDay(history.items), [history.items]);
  const closeModal = () => setOpenModal({ name: null, management: null });
  const openTranscript = (management) => setOpenModal({ name: "transcripcion", management });

  return (
    <Box component="section" aria-label="Historial de gestiones" sx={{ backgroundColor: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: "14px", p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Historial de gestiones</Typography>
          {history.status !== "loading" && (
            <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{`Mostrando ${history.items.length} de ${history.totalElements} gestiones · Horas en hora Colombia`}</Typography>
          )}
        </Box>
      </Box>
      <HistoryFilters filters={filters} credits={credits} onChange={setFilters} onClear={() => setFilters(EMPTY_FILTERS)} />

      {history.status === "loading" && <Skeleton variant="rounded" height={240} />}
      {history.status === "error" && (
        <Box role="alert" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: COLORS.danger }}>No pudimos cargar el historial.</Typography>
          <PrimaryButton onClick={() => loadHistory(0)}>Reintentar</PrimaryButton>
        </Box>
      )}
      {history.status !== "loading" && history.status !== "error" && groups.length === 0 && (
        <Typography sx={{ color: COLORS.muted }}>No hay gestiones con estos filtros.</Typography>
      )}

      {history.status !== "loading" && groups.map((group) => (
        <Box key={group.dateKey}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.muted, py: 1 }}>
            {group.dateKey === "invalid" ? "Sin fecha válida" : formatDate(group.dateKey)}
          </Typography>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {group.entries.map((entry) =>
              entry.type === "attempts" ? (
                <AttemptsBlock key={entry.items[0].uuid} items={entry.items} onTranscript={openTranscript} />
              ) : (
                <ManagementItem key={entry.item.uuid} management={entry.item} onTranscript={openTranscript} />
              )
            )}
          </Box>
        </Box>
      ))}

      {!history.last && history.status !== "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PrimaryButton onClick={() => loadHistory(history.page + 1)} disabled={history.status === "loadingMore"}>
            {history.status === "loadingMore" ? "Cargando…" : `Cargar ${COLLECTION_PAGE_SIZE} más`}
          </PrimaryButton>
        </Box>
      )}

      <TranscripcionModal open={openModal.name === "transcripcion"} management={openModal.management} onClose={closeModal} />
    </Box>
  );
}
